import { periodKey, rowKey, StatementRowRef } from './periods';

import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

interface FoldArgs {
    /** Months already saved, keyed by row — see `rowKey`. */
    saved: Record<string, StatementPeriod | undefined>;
    /**
     * What the member did in this session: a month, or `null` to undo their correction. Keyed by
     * the row the correction applies to, so a document carrying several statements can have one
     * of its months moved without touching the rest.
     *
     * **Iteration order must be edit order.** A month has one owner, and the fold resolves a
     * collision in favour of whichever entry it reaches last — so a caller that updates an
     * existing key in place (a plain spread keeps the original position) would let an earlier
     * correction outrank the member's most recent one. Callers re-insert instead.
     */
    staged: Record<string, StatementPeriod | null>;
    /**
     * Every row any key above can name, so a key never has to be parsed back into the document it
     * belongs to.
     *
     * Callers pass the rows they know about *plus* the rows they staged against, because the two
     * can differ: the row list comes from coverage while the verdicts the member corrects come
     * from the statuses endpoint, and the two refresh independently. A row can be genuinely
     * correctable in a window where coverage has not listed it yet.
     */
    rows: StatementRowRef[];
    /** The documents on screen. A correction on anything else has gone and is dropped. */
    documentUuids: string[];
}

/**
 * Resolve saved months plus this session's edits into one month per row.
 *
 * Derived rather than accumulated: the staged map stores intent only, so a removed file
 * simply stops contributing and cannot leave behind a displacement that outlives it. The same
 * fold draws the rows and builds the request, so what is saved cannot differ from what was on
 * screen. Later edits win; a month can only be held by one row.
 */
export default function foldStagedAssignments({
    saved,
    staged,
    rows,
    documentUuids,
}: FoldArgs): Record<string, StatementPeriod | null> {
    const live = new Set(documentUuids);
    const documentByRowKey = new Map(rows.map((row) => [rowKey(row.documentUuid, row.period), row.documentUuid]));
    /** The document a row key belongs to, or `undefined` once that document has gone. */
    const documentOf = (key: string) => {
        const documentUuid = documentByRowKey.get(key);
        return documentUuid !== undefined && live.has(documentUuid) ? documentUuid : undefined;
    };

    const resolved: Record<string, StatementPeriod | null> = {};

    // Every saved row of a live document, then a row-less entry for a document that has none — so
    // a file with nothing credited still reports an explicit "no month" rather than being absent,
    // and a file with rows is described by those rows alone.
    const documented = new Set<string>();
    Object.entries(saved).forEach(([key, period]) => {
        const documentUuid = documentOf(key);
        if (documentUuid !== undefined) {
            resolved[key] = period ?? null;
            documented.add(documentUuid);
        }
    });
    documentUuids.forEach((documentUuid) => {
        if (!documented.has(documentUuid)) {
            resolved[documentUuid] = null;
        }
    });

    Object.entries(staged).forEach(([key, period]) => {
        const documentUuid = documentOf(key);
        if (documentUuid === undefined) {
            return;
        }
        // Where this row stood before this edit, for the refusal below to put back. Not
        // `saved[key]`: an earlier entry in this same fold may already have displaced this row,
        // and restoring the saved month there left two entries holding one month — which the
        // docblock above says cannot happen, and which the resolver then settles silently.
        const previous = resolved[key] ?? null;
        resolved[key] = period;
        if (!period) {
            return;
        }

        // Everything else holding the claimed month, found once and used twice below.
        const claimed = periodKey(period);
        const holders = Object.entries(resolved).filter(
            ([otherKey, otherPeriod]) => otherKey !== key && otherPeriod && periodKey(otherPeriod) === claimed
        );

        // A sibling row of the *same* document holds it, so the edit is refused and nothing
        // happens. The two alternatives both invent an answer the member did not give: displacing
        // the sibling drops a month they never mentioned, and swapping the two rows asserts an
        // intent that only one of the drags they could have made implies. The menu does not offer
        // such a month, so this is reached only by a staged edit that outlived the state that
        // allowed it, or by the confirmation dialog.
        if (holders.some(([otherKey]) => documentByRowKey.get(otherKey) === documentUuid)) {
            resolved[key] = previous;
            return;
        }

        // Whoever else held it loses it — recomputed from the surviving set every time, so it can
        // never name a row that is no longer here. The early return above means no holder reaching
        // this line belongs to this document, which is what keeps a sibling's month from going.
        holders.forEach(([otherKey]) => {
            resolved[otherKey] = null;
        });
    });

    return resolved;
}

/**
 * Only the rows whose month differs from what is saved. An unchanged `null` would
 * otherwise read as an undo of a correction that was never made.
 */
export function changedAssignments(
    resolved: Record<string, StatementPeriod | null>,
    saved: Record<string, StatementPeriod | undefined>
): Record<string, StatementPeriod | null> {
    return Object.fromEntries(
        Object.entries(resolved).filter(([key, period]) => {
            const current = saved[key];
            if (!period) {
                return current != null;
            }
            return !current || periodKey(current) !== periodKey(period);
        })
    );
}
